import React from "react";
import {useTranslation} from "react-i18next";
import RequireAuth from "../../components/RequireAuth";
import {useNavigate} from "react-router-dom";
import {useSnackbar} from "notistack";
import * as yup from "yup";
import {useFormik} from "formik";
import {alpha, Box, Button, Stack, TextField, Toolbar, Typography} from "@mui/material";
import {usePasswordUpdateMutation} from "../../app/api/api";

export default function UpdatePassword() {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [passwordUpdate] = usePasswordUpdateMutation();
    const {enqueueSnackbar} = useSnackbar();

    const validationSchema = yup.object({
        currentPassword: yup.string().required(t("validation_inputRequired")),
        password: yup.string().required(t("validation_inputRequired")),
        passwordConfirmation: yup
            .string()
            .required(t("validation_inputRequired"))
            .oneOf([yup.ref("password"), null], t("validation_passwordMustMatch")),
    });

    const formik = useFormik({
        initialValues: {
            currentPassword: "",
            password: "",
            passwordConfirmation: "",
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            const data = {
                oldPassword: values.currentPassword,
                newPassword: values.password,
            };
            await passwordUpdate(data).unwrap();
            enqueueSnackbar(t("generic_savedSuccessfully"), {variant: "success"});
            navigate("/");
        },
    });

    return (
        <RequireAuth>
            <Box>
                <Toolbar
                    sx={{
                        pl: {sm: 2},
                        pr: {xs: 1, sm: 1},
                        ...{
                            bgcolor: (theme) =>
                                alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
                        },
                    }}
                >
                    <Typography variant="h6" id="tableTitle" component="div">
                        {t("action_updatePassword")}
                    </Typography>
                </Toolbar>
                <form onSubmit={formik.handleSubmit}>
                    <Stack
                        spacing={3}
                        width={500}
                        sx={{
                            mt: 3,
                        }}
                    >
                        <TextField
                            type={"password"}
                            fullWidth
                            id="currentPassword"
                            name="currentPassword"
                            label={t("user_currentPassword")}
                            value={formik.values.currentPassword}
                            onChange={formik.handleChange}
                            error={formik.touched.currentPassword && Boolean(formik.errors.currentPassword)}
                            helperText={formik.touched.currentPassword && formik.errors.currentPassword}
                        />
                        <TextField
                            type={"password"}
                            fullWidth
                            id="password"
                            name="password"
                            label={t("user_newPassword")}
                            value={formik.values.password}
                            onChange={formik.handleChange}
                            error={formik.touched.password && Boolean(formik.errors.password)}
                            helperText={formik.touched.password && formik.errors.password}
                        />
                        <TextField
                            type={"password"}
                            fullWidth
                            id="passwordConfirmation"
                            name="passwordConfirmation"
                            label={t("user_passwordConfirmation")}
                            value={formik.values.passwordConfirmation}
                            onChange={formik.handleChange}
                            error={formik.touched.passwordConfirmation && Boolean(formik.errors.passwordConfirmation)}
                            helperText={formik.touched.passwordConfirmation && formik.errors.passwordConfirmation}
                        />
                        <Box sx={{display: "flex", justifyContent: "begin"}}>
                            <Button color="primary" variant="contained" type="submit">
                                {t("action_save")}
                            </Button>
                        </Box>
                    </Stack>
                </form>
            </Box>
        </RequireAuth>
    );
}
